import { Plus, School, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { DataTable } from "../../../components/DataTable";
import { Modal } from "../../../components/Modal";
import { Notification } from "../../../components/Notification";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { apiClient } from "../../../lib";
import {
  ExternalComuna,
  ExternalRegion,
  getExternalComunasByRegion,
  getExternalRegions,
} from "../../../lib/externalApi";
import { AdminLayout } from "../components";

interface Colegio {
  idColegio: number;
  nombre: string;
  direccion?: string;
  vigente: boolean;
  comuna: {
    idComuna: number;
    nombre: string;
    region: {
      nombre: string;
    };
  };
  historialAcademico: Array<{
    usuario: {
      idUsuario: number;
      nombres: string;
      apellidos: string;
      email: string;
      rol: string;
    };
  }>;
}

export const ColegiosPage: React.FC = () => {
  const [colegios, setColegios] = useState<Colegio[]>([]);
  const [externalRegions, setExternalRegions] = useState<ExternalRegion[]>([]);
  const [externalComunas, setExternalComunas] = useState<ExternalComuna[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingColegio, setEditingColegio] = useState<Colegio | null>(null);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  } | null>(null);

  // Filtros
  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  const [filtroVigencia, setFiltroVigencia] = useState("todos");

  // Formulario
  const [formData, setFormData] = useState({
    nombre: "",
    direccion: "",
    regionId: "",
    comunaId: "",
    vigente: true,
  });

  const showNotification = useCallback(
    (type: "success" | "error", message: string) => {
      setNotification({
        isOpen: true,
        type,
        title: type === "success" ? "Éxito" : "Error",
        message,
      });
    },
    []
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [colegiosRes, regionsRes] = await Promise.all([
        apiClient.get("/admin/colegios"),
        getExternalRegions(),
      ]);

      // Filtrar elementos inválidos y asegurar estructura correcta
      const colegiosData = (colegiosRes.data.data || []).filter(
        (colegio: any) => colegio != null
      );
      setColegios(colegiosData);
      setExternalRegions(regionsRes);
    } catch (error) {
      console.error("Error cargando datos:", error);
      showNotification("error", "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadColegios = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtroBusqueda) params.append("search", filtroBusqueda);
      if (filtroVigencia !== "todos") params.append("vigente", filtroVigencia);

      const response = await apiClient.get(`/admin/colegios?${params}`);
      // Filtrar elementos inválidos y asegurar estructura correcta
      const colegiosData = (response.data.data || []).filter(
        (colegio: any) => colegio != null
      );
      setColegios(colegiosData);
    } catch (error) {
      console.error("Error cargando colegios:", error);
      showNotification("error", "Error cargando colegios");
    }
  }, [filtroBusqueda, filtroVigencia, showNotification]);

  useEffect(() => {
    loadColegios();
  }, [loadColegios]);

  // Cargar comunas cuando se selecciona una región
  const handleRegionChange = async (regionId: string) => {
    setFormData((prev) => ({ ...prev, regionId, comunaId: "" }));

    if (regionId) {
      try {
        const comunas = await getExternalComunasByRegion(parseInt(regionId));
        setExternalComunas(comunas);
      } catch (error) {
        console.error("Error cargando comunas:", error);
        showNotification("error", "Error cargando comunas");
      }
    } else {
      setExternalComunas([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingColegio) {
        await apiClient.put(`/admin/colegios/${editingColegio.idColegio}`, {
          nombre: formData.nombre,
          direccion: formData.direccion,
          comunaId: formData.comunaId,
          vigente: formData.vigente,
        });
        showNotification("success", "Colegio actualizado exitosamente");
      } else {
        await apiClient.post("/admin/colegios", {
          nombre: formData.nombre,
          direccion: formData.direccion,
          comunaId: formData.comunaId,
          vigente: formData.vigente,
        });
        showNotification("success", "Colegio creado exitosamente");
      }

      setShowModal(false);
      resetForm();
      loadColegios();
    } catch (error: any) {
      console.error("Error guardando colegio:", error);
      showNotification(
        "error",
        error.response?.data?.message || "Error guardando colegio"
      );
    }
  };

  const handleEdit = (colegio: Colegio) => {
    setEditingColegio(colegio);
    setFormData({
      nombre: colegio.nombre || "",
      direccion: colegio.direccion || "",
      regionId: "", // Se calculará basado en la comuna
      comunaId: colegio.comuna?.idComuna?.toString() || "", // Usar el ID real
      vigente: colegio.vigente ?? true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    // Reemplazar confirm() con una implementación personalizada
    const userConfirmed = window.confirm(
      "¿Estás seguro de que quieres eliminar este colegio? Esta acción no se puede deshacer."
    );

    if (!userConfirmed) return;

    try {
      await apiClient.delete(`/admin/colegios/${id}`);
      showNotification("success", "Colegio eliminado exitosamente");
      loadColegios();
    } catch (error) {
      console.error("Error eliminando colegio:", error);
      showNotification("error", "Error eliminando colegio");
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: "",
      direccion: "",
      regionId: "",
      comunaId: "",
      vigente: true,
    });
    setEditingColegio(null);
    setExternalComunas([]);
  };

  const getRolLabel = (rol: string) => {
    switch (rol) {
      case "ALUMNO":
        return "Estudiante";
      case "PROFESOR":
        return "Profesor";
      case "ADMIN":
        return "Administrador";
      default:
        return rol;
    }
  };

  const getRolColor = (rol: string) => {
    switch (rol) {
      case "ALUMNO":
        return "bg-blue-100 text-blue-800";
      case "PROFESOR":
        return "bg-green-100 text-green-800";
      case "ADMIN":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = [
    {
      key: "nombre",
      label: "Nombre",
      type: "text" as const,
      cell: ({ row }: any) => (
        <div className="font-medium">{row?.nombre || "-"}</div>
      ),
    },
    {
      key: "direccion",
      label: "Dirección",
      type: "text" as const,
      cell: ({ row }: any) => (
        <div className="text-sm text-gray-700">
          {row?.direccion || "-"}
        </div>
      ),
    },
    {
      key: "ubicacion",
      label: "Ubicación",
      type: "text" as const,
      cell: ({ row }: any) => (
        <div>
          <div className="font-medium">
            {row?.comuna?.nombre || "-"}
          </div>
          <div className="text-sm text-gray-500">
            {row?.comuna?.region?.nombre || "-"}
          </div>
        </div>
      ),
    },
    {
      key: "vigencia",
      label: "Vigencia",
      type: "text" as const,
      cell: ({ row }: any) => {
        const vigente = row?.vigente ?? false;
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              vigente
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {vigente ? "Vigente" : "No Vigente"}
          </span>
        );
      },
    },
    {
      key: "usuarios",
      label: "Usuarios",
      type: "text" as const,
      cell: ({ row }: any) => {
        const usuarios = row?.historialAcademico || [];

        const estudiantes = usuarios.filter(
          (u: any) => u.usuario?.rol === "ALUMNO"
        ).length;
        const profesores = usuarios.filter(
          (u: any) => u.usuario?.rol === "PROFESOR"
        ).length;

        return (
          <div>
            <div className="flex space-x-2">
              <span className="text-sm text-blue-600">
                {estudiantes} estudiantes
              </span>
              <span className="text-sm text-green-600">
                {profesores} profesores
              </span>
            </div>
            <div className="text-xs text-gray-500">
              Total: {usuarios.length} usuarios
            </div>
          </div>
        );
      },
    },
    {
      key: "usuariosActivos",
      label: "Usuarios Activos",
      type: "text" as const,
      cell: ({ row }: any) => {
        const usuarios = row?.historialAcademico || [];

        if (!usuarios || usuarios.length === 0) {
          return (
            <div className="text-sm text-gray-500">Sin usuarios activos</div>
          );
        }

        return (
          <div className="space-y-1">
            {usuarios.slice(0, 3).map((historial: any) => (
              <div
                key={historial.usuario?.idUsuario || Math.random()}
                className="flex items-center space-x-2"
              >
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getRolColor(
                    historial.usuario?.rol || "ALUMNO"
                  )}`}
                >
                  {getRolLabel(historial.usuario?.rol || "ALUMNO")}
                </span>
                <span className="text-sm text-black">
                  {historial.usuario?.nombres || "N/A"}{" "}
                  {historial.usuario?.apellidos || "N/A"}
                </span>
              </div>
            ))}
            {usuarios.length > 3 && (
              <div className="text-xs text-gray-500">
                +{usuarios.length - 3} más...
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "acciones",
      label: "Acciones",
      type: "actions" as const,
    },
  ];

  const actions = [
    {
      label: "Editar",
      onClick: (item: any) => handleEdit(item),
      variant: "edit" as const,
    },
    {
      label: "Eliminar",
      onClick: (item: any) => handleDelete(item.idColegio),
      variant: "delete" as const,
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestión de Colegios
            </h1>
            <p className="text-gray-600">Administra los colegios del sistema</p>
          </div>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Crear Colegio
          </Button>
        </div>

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="busqueda">Búsqueda</Label>
                <Input
                  id="busqueda"
                  placeholder="Nombre del colegio..."
                  value={filtroBusqueda}
                  onChange={(e) => setFiltroBusqueda(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vigencia">Vigencia</Label>
                <Select
                  value={filtroVigencia}
                  onValueChange={setFiltroVigencia}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los colegios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los colegios</SelectItem>
                    <SelectItem value="true">Solo vigentes</SelectItem>
                    <SelectItem value="false">Solo no vigentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={loadColegios}>
                  <Search className="h-4 w-4 mr-2" />
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabla de colegios */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <School className="h-5 w-5" />
              Colegios ({colegios.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              title="Colegios"
              columns={columns}
              data={colegios}
              actions={actions}
            />
          </CardContent>
        </Card>

        {/* Modal de creación/edición */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            resetForm();
          }}
          title={editingColegio ? "Editar Colegio" : "Crear Colegio"}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre del Colegio *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="direccion">Dirección (Opcional)</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) =>
                    setFormData({ ...formData, direccion: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="vigencia">Vigencia *</Label>
                <Select
                  value={formData.vigente ? "vigente" : "no_vigente"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, vigente: value === "vigente" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar vigencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vigente">Vigente</SelectItem>
                    <SelectItem value="no_vigente">No Vigente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="region">Región *</Label>
                <Select
                  value={formData.regionId}
                  onValueChange={handleRegionChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar región" />
                  </SelectTrigger>
                  <SelectContent>
                    {externalRegions.map((region) => (
                      <SelectItem key={region.id} value={region.id.toString()}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="comuna">Comuna *</Label>
                <Select
                  value={formData.comunaId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, comunaId: value })
                  }
                  disabled={!formData.regionId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar comuna" />
                  </SelectTrigger>
                  <SelectContent>
                    {externalComunas.map((comuna) => (
                      <SelectItem key={comuna.id} value={comuna.id.toString()}>
                        {comuna.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingColegio ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Notificación */}
        {notification && (
          <Notification
            isOpen={notification.isOpen}
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};
